import { Suspense, lazy, Component, useMemo, useState, useEffect, useRef } from "react";
import "./SplineScene.css";

const Spline = lazy(() => import("@splinetool/react-spline"));

/**
 * Detect WebGL2 availability once and cache the result.
 * Spline requires WebGL2. Also rejects software/SwiftShader renderers
 * that create a context but fail on Spline's GPU-specific draw calls.
 */
const checkWebGL = () => {
  try {
    const canvas = document.createElement("canvas");
    // Spline requires WebGL2 — don't fall back to WebGL1
    const gl = canvas.getContext("webgl2");
    if (!gl) return false;
    // Reject software renderers (SwiftShader / llvmpipe) — they report
    // a valid context but Spline's renderer throws during initialisation.
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) {
      const renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "";
      if (/SwiftShader|llvmpipe|softpipe|software|Mesa.*softpipe/i.test(renderer)) {
        console.info("[SplineScene] Software WebGL renderer detected (" + renderer + "), skipping 3D.");
        gl.getExtension("WEBGL_lose_context")?.loseContext();
        return false;
      }
    }
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
};
const HAS_WEBGL = checkWebGL();

/**
 * Class‑based Error Boundary that catches the WebGL runtime throw
 * from the Spline library and falls back gracefully.
 */
class SplineErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // Log the actual error so we can debug why it failed
    console.warn(
      "[SplineScene] 3D render failed, showing static fallback.\n" +
      "Error:", error?.message || error,
      "\nThis usually means WebGL is unavailable or context creation failed.",
      "\nDetails:", info
    );
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

/** Static visual fallback when 3D can't render or is slow to load */
const StaticFallback = () => (
  <div className="spline-static-fallback">
    <div className="spline-fallback-icon">🤖</div>
    <div className="spline-fallback-text">Interactive 3D view loading...</div>
  </div>
);

/**
 * Inner wrapper that adds a load‑timeout.
 * If Spline doesn't fire onLoad within TIMEOUT_MS, we swap to the static fallback
 * so the user doesn't stare at a spinner forever on slow connections.
 */
const TIMEOUT_MS = 20000; // 20 seconds (generous for slow networks/LANs)

const SplineWithTimeout = ({ scene, className }) => {
  const [timedOut, setTimedOut] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [asyncError, setAsyncError] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    console.log("[SplineScene] Starting 3D scene load:", scene);
  }, [scene]);

  // Intercept console.error so we catch Spline's internal "Error creating WebGL context"
  // log (which Spline swallows as a caught error and never re-throws as a Promise rejection).
  useEffect(() => {
    const original = console.error;
    console.error = (...args) => {
      const msg = args.map(a => (a && a.message) || String(a)).join(" ");
      if (/webgl|creating.*context|context.*creat/i.test(msg)) {
        console.warn("[SplineScene] Intercepted WebGL console.error — showing static fallback.");
        setAsyncError(true);
        console.error = original; // restore immediately
        return;
      }
      original.apply(console, args);
    };
    return () => { console.error = original; };
  }, []);

  // Catch async WebGL errors that escape the React error boundary
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.reason?.message || e?.message || String(e?.reason || "");
      if (/webgl|context/i.test(msg)) {
        console.warn("[SplineScene] Caught async WebGL error, showing fallback.");
        e.preventDefault?.();
        setAsyncError(true);
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  useEffect(() => {
    timer.current = setTimeout(() => {
      if (!loaded) {
        console.warn(
          `[SplineScene] 3D scene didn't load within ${TIMEOUT_MS / 1000}s, showing static fallback.\n` +
          "This usually means slow network or CDN issues."
        );
        setTimedOut(true);
      }
    }, TIMEOUT_MS);
    return () => clearTimeout(timer.current);
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      console.log("[SplineScene] 3D scene loaded successfully ✓");
    }
  }, [loaded]);

  if (asyncError || (timedOut && !loaded)) {
    return <StaticFallback />;
  }

  return (
    <Spline
      scene={scene}
      className={className}
      onLoad={() => {
        setLoaded(true);
        clearTimeout(timer.current);
      }}
    />
  );
};

/**
 * SplineScene – lazy-loaded Spline 3D scene with:
 *  • Error boundary (catches WebGL runtime errors and shows static fallback)
 *  • Load timeout (shows static fallback if 3D doesn't load in 20s - handles slow networks)
 *  • Static visual fallback (floating robot emoji) so the section never looks broken
 * 
 * This always ATTEMPTS to load the 3D scene. Only falls back if:
 *  1. WebGL context creation fails at runtime (caught by error boundary)
 *  2. Scene doesn't load within 20s timeout (slow network/CDN)
 *  3. Any other Spline library error
 *
 * @param {{ scene: string, className?: string }} props
 */
export function SplineScene({ scene, className }) {
  const loaderUI = useMemo(
    () => (
      <div className="spline-scene-loader">
        <span className="spline-spinner"></span>
      </div>
    ),
    []
  );

  const staticFallback = useMemo(() => <StaticFallback />, []);

  // Skip 3D entirely if WebGL is unavailable – avoids async runtime errors
  if (!HAS_WEBGL) {
    console.warn("[SplineScene] WebGL not available, rendering static fallback.");
    return staticFallback;
  }

  // Always try to load - let the error boundary and timeout handle failures
  return (
    <SplineErrorBoundary fallback={staticFallback}>
      <Suspense fallback={loaderUI}>
        <SplineWithTimeout scene={scene} className={className} />
      </Suspense>
    </SplineErrorBoundary>
  );
}
